import { Component, computed, EventEmitter, HostListener, input, Output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { AddZToUTCPipe } from 'libs/shared/util-common/src/lib/pipes/add-z-to-utc.pipe';
import { ReferenceAuthorNamePipe } from 'libs/shared/util-common/src/lib/pipes/reference-author-name.pipe';

import { Reference, ReferenceType, ReferenceWithNumber } from '@treetop/shared/model';

import { IconDirective } from '../icon/icon.directive';
import { TooltipDirective } from '../tooltip/tooltip.directive';

@Component({
  selector: 'tt-reference-card',
  standalone: true,
  imports: [TooltipDirective, ReferenceAuthorNamePipe, TranslocoPipe, MatIconModule, IconDirective, TranslocoDatePipe, AddZToUTCPipe],
  templateUrl: './reference-card.component.html',
  styleUrl: './reference-card.component.scss',
  host: {
    class: 'tt-reference-card',
    '[class.reference-card--selectable]': 'selectable()',
    '[class.reference-card--selected]': 'selected()',
    '[class.reference-card--disabled]': 'disabled()',
  },
})
export class ReferenceCardComponent {
  public reference = input.required<ReferenceWithNumber>();
  public deletable = input<boolean>(false);
  public editable = input<boolean>(false);
  public selectable = input<boolean>(false);
  public selected = input<boolean>(false);
  public disabled = input<boolean>(false);

  @HostListener('click')
  public onClick() {
    if (this.selectable() && !this.disabled()) {
      this.selectReference.emit();
    }
  }

  @Output() public selectReference = new EventEmitter<void>();
  @Output() public deleteReference = new EventEmitter<Reference>();
  @Output() public editReference = new EventEmitter<Reference>();

  protected authorsExpanded = signal(false);

  protected publisher = computed(() => {
    const reference = this.reference();

    switch (reference.referenceType) {
      case ReferenceType.JournalArticle:
        return reference.extendedContent.journalAbbreviation;
      case ReferenceType.Book:
        return reference.extendedContent.publisher;
      case ReferenceType.BookSection:
        return reference.extendedContent.bookTitle;
      case ReferenceType.ConferencePaper:
        return reference.extendedContent.conferenceName;
      case ReferenceType.Report:
        return reference.extendedContent.institution;
      case ReferenceType.BlogPost:
        return reference.extendedContent.blogTitle;
      case ReferenceType.WebPage:
        return reference.extendedContent.websiteTitle;
    }
  });

  protected link = computed(() => {
    const reference = this.reference();

    if (reference.url) {
      return reference.url;
    } else if (reference.doi) {
      return `https://doi.org/${reference.doi}`;
    }

    return undefined;
  });

  protected authors = computed(() => {
    return this.reference().authors ?? [];
  });
}
